import React, { useState, useEffect, Fragment } from 'react'
import Sticky from 'react-sticky-el'
import { Link, Image } from 'newComponents'
import generatePath from '../../../../config/generatePath'
import chevron from '../../../images/ebook/thin-chevron.svg'

import './styles.scss'

const getIdFromTitle = (title) => (title ? title.replace(/\s+/g, '-').toLowerCase() : '')

const TableOfContents = ({ anchorPoints, pages, chapterIndex }) => {
  // //////////// DATA FORMATTING ////////////////////

  const chapterList = []
  pages.forEach((chapter) => {
    const { data, uid } = chapter
    const { title } = data
    chapterList.push({ uid, title: title.text })
  })

  const titleList = []
  anchorPoints.forEach((point) => {
    const { title } = point
    titleList.push(title.text)
  })

  // create ids out of titleList items
  const links = titleList.map((label) => ({ id: getIdFromTitle(label), label }))

  const arrangedList = []
  anchorPoints.forEach((point) => {
    const { is_child: isChild, title } = point

    if (isChild) {
      const lastParent = arrangedList[arrangedList.length - 1]
      // If no parent has been pushed, make this item a parent to account for bad data entry
      if (!lastParent) {
        return arrangedList.push({ title: title.text, sublinks: [] })
      }
      // Else push item to last parent
      return lastParent.sublinks.push({ title: title.text })
    }
    // Handle is parent logic
    return arrangedList.push({ title: title.text, sublinks: [] })
  })

  // //////////// mobile menu toggle //////////
  const [menuOpen, setMenuOpen] = useState(false)

  const toggleMenu = (event) => {
    event.preventDefault()
    if (menuOpen) {
      setMenuOpen(false)
      document.body.classList.remove('ebook-menu-open')
    }
    if (!menuOpen) {
      setMenuOpen(true)
      document.body.classList.add('ebook-menu-open')
    }
  }

  useEffect(() => () => document.body.classList.remove('ebook-menu-open'), [])

  const mobileNavToggle = menuOpen ? 'open' : ''

  return (
    <Sticky stickyClassName="sticky" boundaryElement=".ebook" hideOnBoundaryHit={false} className="toc">
      <div className={`toc-container ${mobileNavToggle}`}>
        {chapterList.map((chapter, index) => {
          const { uid, title: chapterTitle } = chapter
          const isChapter = chapterIndex === index + 1 ? 'current-chapter' : ''
          return (
            <Fragment key={chapterTitle}>
              <div className={`toc-chapter-title ${isChapter}`}>
                <Link to={generatePath(uid)}>{chapterTitle}</Link>
                {/* first title/book title on mobile becomes menu toggle */}
                {index === 0 && (
                  <button type="button" onClick={toggleMenu} className="toc-mobile-menu-toggle">
                    <Image className="toc-chapter-title-chevron" image={{ url: chevron }} />
                  </button>
                )}
              </div>
              {chapterIndex === index + 1 && (
                <AnchorList arrangedList={arrangedList} titleList={titleList} links={links} />
              )}
            </Fragment>
          )
        })}
      </div>
    </Sticky>
  )
}

// ANCHOR TITLE LIST
export const AnchorList = ({ arrangedList, titleList, links }) => {
  // state
  const [linkElements, setLinkElements] = useState(links)
  const [active, setActive] = useState(null)
  const [headerHeight, setHeaderHeight] = useState(0)

  useEffect(() => {
    window.addEventListener('scroll', scrollListener)
    // Set header height to state to offset against anchor point positioning
    const headerElement = document.querySelector('.header-nav')
    const additionalBuffer = 30
    if (headerElement) setHeaderHeight(headerElement.offsetHeight + additionalBuffer)
    // Tidy up when unmounting component from DOM
    return () => {
      window.removeEventListener('scroll', scrollListener)
    }
  }, [])

  useEffect(() => {
    // Fetch offsetTop values for elements matching sidebar ids
    getLinkOffsets()
  }, [headerHeight])

  const getLinkOffsets = () => {
    // Loop over sidebar items and get the offsetTop of the related divs
    titleList.forEach((anchorPoint, index) => {
      const anchorId = getIdFromTitle(anchorPoint)
      const linkElement = document.getElementById(anchorId)
      const offset = window.pageYOffset + linkElement.getBoundingClientRect().top
      linkElements[index].offset = linkElement ? offset - headerHeight : 9999
    })
    // Save sideLinks with offset keys to state
    setLinkElements(linkElements)
    setActive(active || linkElements[0])
  }

  const scrollListener = () => {
    const scrollPosition = window.pageYOffset
    const top = scrollPosition
    const bottomOfPage = scrollPosition + window.innerHeight === document.documentElement.scrollHeight
    const lastItem = linkElements[linkElements.length - 1]

    const getActive = (link, index, self) => {
      // First sidebar link selected when scroll is less than first item offset
      if (index === 0 && top < link.offset) return true
      // Last sidebar link selected when check reaches end of array
      if (!self[index + 1]) return true
      // Check whether top falls between active and next item's offset
      return top >= link.offset && top <= self[index + 1].offset
    }

    const scrollActive = bottomOfPage ? lastItem : linkElements.find(getActive)

    if (scrollActive && scrollActive !== active) {
      setActive(scrollActive)
    }
  }

  const handleClick = (event, anchorId, backToTop) => {
    event.preventDefault()
    // Is first item
    if (backToTop) {
      return window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const anchorElement = document.getElementById(`${anchorId}`)
    // Ensure it scrolls to a point where the active nav item updates correctly (purpose of the -10)
    const y = anchorElement.getBoundingClientRect().top + window.pageYOffset - (headerHeight - 10)
    window.scrollTo({ top: y, behavior: 'smooth' })
  }

  return (
    <div className="toc-anchor-titles">
      <ul className="anchor-title-list">
        {/* PARENT ANCHOR titles */}
        {arrangedList.map((anchorPoint, index) => {
          const { title, sublinks = [] } = anchorPoint
          const primaryAnchorId = getIdFromTitle(title)
          const activeTitle = active && active.label
          const hasActiveSublink = sublinks.find((sublink) => sublink.title === activeTitle)
          const isActive = activeTitle === title || hasActiveSublink
          const currentActiveTitle = activeTitle === title
          return (
            <Fragment key={title}>
              <a
                key={primaryAnchorId}
                href={`#${primaryAnchorId}`}
                className={`toc-anchor-container ${isActive ? 'active' : 'inactive'} ${
                  currentActiveTitle ? 'current' : ''
                }`}
                onClick={(event) => handleClick(event, primaryAnchorId, index === 0)}
              >
                <li className="toc-anchor-title">{title}</li>
              </a>
              {/* CHILD ANCHOR titles */}
              {sublinks.length > 0 && (
                <ul className="child-links">
                  {sublinks.map((sublink) => {
                    const { title: childTitle } = sublink
                    const childAnchorId = getIdFromTitle(childTitle)
                    const sublinkActive = active && active.label === childTitle
                    const currentActiveSubtitle = activeTitle === childTitle
                    return (
                      <li className="toc-anchor-title">
                        <a
                          key={childAnchorId}
                          href={`#${childAnchorId}`}
                          className={`toc-anchor-container ${sublinkActive ? 'active' : 'inactive'}
                            ${currentActiveSubtitle ? 'current' : ''} is-child`}
                          onClick={(event) => handleClick(event, childAnchorId)}
                        >
                          {childTitle}
                        </a>
                      </li>
                    )
                  })}
                </ul>
              )}
            </Fragment>
          )
        })}
      </ul>
    </div>
  )
}

export default TableOfContents
